import { useEffect, useState, type ReactNode } from 'react';
import { useFormatDateTime } from '../util/displayTimeZone';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import * as sessionsApi from '../api/sessions';
import * as baselinesApi from '../api/baselines';
import * as orgApi from '../api/organizations';
import { extractErrorMessage } from '../api/errors';
import { AttachmentsDialog } from '../components/AttachmentsDialog';
import { SessionReplayDialog } from '../components/SessionReplayDialog';
import { SplitSessionDialog } from '../components/SplitSessionDialog';
import type { TimelineEvent } from '../components/CaptureTimeline';
import { SearchableSelect } from '../components/SearchableSelect';

const NONE = [{ value: '', label: '— none —' }];
const KIND_OPTIONS = sessionsApi.SESSION_KINDS.map((k) => ({
  value: k as string,
  label: sessionsApi.SESSION_KIND_LABELS[k],
}));

/**
 * Shared capture-timeline actions (download / attach / replay / split / tag /
 * disable / restore) + their dialogs. Both the tenant Activity page and the
 * Dashboard Individual-mode drill-down use this hook so the two surfaces
 * offer the identical action menu and can't drift.
 *
 * Spread `actions` onto CaptureTimeline (in `actionMenu` mode), render
 * `dialogs` anywhere in the tree, and surface `error`. Write actions are
 * gated by `canWrite` (read-only admins get only Replay + baseline download);
 * `onChanged` fires after a mutation so the caller can refetch.
 */

export function anchorClickDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function downloadBaselineJsonRow(baseline: baselinesApi.Baseline): void {
  const safeParticipant = (baseline.participant_label ?? 'baseline').replace(
    /[^A-Za-z0-9._-]+/g,
    '_',
  );
  const captured = baseline.captured_at.replace(/[:.]/g, '-');
  const filename = `${safeParticipant}-baseline-${captured}.json`;
  const blob = new Blob([JSON.stringify(baseline, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface CaptureActions {
  onReplay: (e: TimelineEvent) => void;
  onDownload?: (e: TimelineEvent) => void;
  onAttach?: (e: TimelineEvent) => void;
  onSplit?: (e: TimelineEvent) => void;
  onTag?: (e: TimelineEvent) => void;
  onDisable?: (e: TimelineEvent) => void;
  onRestore?: (e: TimelineEvent) => void;
  busyId: string | null;
}

export function useCaptureActions({
  tenantId,
  canWrite,
  onChanged,
}: {
  tenantId: string;
  canWrite: boolean;
  onChanged: () => void | Promise<void>;
}): {
  actions: CaptureActions;
  dialogs: ReactNode;
  error: string | null;
  /** Fetched once for the tag dialog — also handy for the caller's own
   *  department/location name lookups (e.g. the Activity timeline). */
  departments: orgApi.Department[];
  locations: orgApi.Location[];
} {
  const fmtDateTime = useFormatDateTime();
  const [attachEvent, setAttachEvent] = useState<TimelineEvent | null>(null);
  const [replayEvent, setReplayEvent] = useState<TimelineEvent | null>(null);
  const [splitEvent, setSplitEvent] = useState<TimelineEvent | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<TimelineEvent | null>(null);

  const [departments, setDepartments] = useState<orgApi.Department[]>([]);
  const [locations, setLocations] = useState<orgApi.Location[]>([]);
  const [tagEvent, setTagEvent] = useState<TimelineEvent | null>(null);
  const [tagDept, setTagDept] = useState('');
  const [tagLoc, setTagLoc] = useState('');
  const [tagKind, setTagKind] = useState('');
  const [tagRef, setTagRef] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      orgApi.listDepartments(tenantId).catch(() => []),
      orgApi.listLocations(tenantId).catch(() => []),
    ]).then(([d, l]) => {
      if (cancelled) return;
      setDepartments(d);
      setLocations(l);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleDownload(event: TimelineEvent) {
    setDownloadingId(event.id);
    setDownloadError(null);
    try {
      if (event.kind === 'session') {
        const { url, filename } = await sessionsApi.getCaptureSessionDownload(
          tenantId,
          event.id,
        );
        anchorClickDownload(url, filename);
      } else if (event.s3Key !== null) {
        const { url, filename } = await baselinesApi.getBaselineDownload(
          tenantId,
          event.id,
        );
        anchorClickDownload(url, filename);
      } else {
        downloadBaselineJsonRow(event.raw as baselinesApi.Baseline);
      }
    } catch (err: unknown) {
      setDownloadError(extractErrorMessage(err, 'download failed'));
    } finally {
      setDownloadingId(null);
    }
  }

  async function confirmDisable() {
    if (!disableTarget) return;
    const event = disableTarget;
    setMutatingId(event.id);
    setActionError(null);
    try {
      if (event.kind === 'session') {
        await sessionsApi.disableCaptureSession(tenantId, event.id);
      } else {
        await baselinesApi.disableBaseline(tenantId, event.id);
      }
      setDisableTarget(null);
      await onChanged();
    } catch (err: unknown) {
      setActionError(extractErrorMessage(err, 'disable failed'));
      setDisableTarget(null);
    } finally {
      setMutatingId(null);
    }
  }

  async function handleRestore(event: TimelineEvent) {
    setMutatingId(event.id);
    setActionError(null);
    try {
      if (event.kind === 'session') {
        await sessionsApi.restoreCaptureSession(tenantId, event.id);
      } else {
        await baselinesApi.restoreBaseline(tenantId, event.id);
      }
      await onChanged();
    } catch (err: unknown) {
      setActionError(extractErrorMessage(err, 'restore failed'));
    } finally {
      setMutatingId(null);
    }
  }

  function handleTag(event: TimelineEvent) {
    setTagEvent(event);
    setTagDept(event.departmentId ?? '');
    setTagLoc(event.locationId ?? '');
    setTagKind(event.sessionKind ?? '');
    setTagRef(event.externalCaseRef ?? '');
    setTagError(null);
  }

  async function submitTag() {
    if (!tagEvent) return;
    setTagSaving(true);
    setTagError(null);
    try {
      await sessionsApi.tagCaptureSession(tenantId, tagEvent.id, {
        department_id: tagDept || null,
        location_id: tagLoc || null,
        kind: tagKind || null,
        external_case_ref: tagRef.trim() || null,
      });
      setTagEvent(null);
      await onChanged();
    } catch (err: unknown) {
      setTagError(extractErrorMessage(err, 'failed to tag session'));
    } finally {
      setTagSaving(false);
    }
  }

  const actions: CaptureActions = {
    onReplay: (e) => setReplayEvent(e),
    onDownload: canWrite ? handleDownload : undefined,
    onAttach: canWrite ? (e) => setAttachEvent(e) : undefined,
    onSplit: canWrite ? (e) => setSplitEvent(e) : undefined,
    onTag: canWrite ? handleTag : undefined,
    onDisable: canWrite
      ? (e) => {
          setActionError(null);
          setDisableTarget(e);
        }
      : undefined,
    onRestore: canWrite ? handleRestore : undefined,
    busyId: downloadingId ?? mutatingId,
  };

  const dialogs = (
    <>
      {attachEvent && (
        <AttachmentsDialog
          open
          onClose={() => setAttachEvent(null)}
          sessionRowId={attachEvent.id}
          sessionLabel={`${
            attachEvent.procedureName ?? 'Session'
          } · ${fmtDateTime(attachEvent.capturedAt)}`}
          tenantId={tenantId}
        />
      )}
      {replayEvent && (
        <SessionReplayDialog
          open
          onClose={() => setReplayEvent(null)}
          title={`${replayEvent.procedureName ?? 'Session'}${
            replayEvent.participantLabel
              ? ` · ${replayEvent.participantLabel}`
              : ''
          } · ${fmtDateTime(replayEvent.capturedAt)}`}
          fetchUrl={() =>
            sessionsApi.getSessionReplayUrl(tenantId, replayEvent.id)
          }
        />
      )}
      {splitEvent && (
        <SplitSessionDialog
          open
          onClose={() => setSplitEvent(null)}
          tenantId={tenantId}
          event={splitEvent}
          onSplitDone={() => void onChanged()}
        />
      )}

      <Dialog
        open={tagEvent !== null}
        onClose={() => (tagSaving ? null : setTagEvent(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Department &amp; location</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tag this recording with where it happened, so activity can be
            organized and compared.
          </DialogContentText>
          <Stack spacing={2}>
            <SearchableSelect
              label="Department"
              fullWidth
              value={tagDept}
              onChange={setTagDept}
              disabled={tagSaving}
              pinned={NONE}
              options={departments.map((d) => ({ value: d.id, label: d.name }))}
            />
            <SearchableSelect
              label="Location"
              fullWidth
              value={tagLoc}
              onChange={setTagLoc}
              disabled={tagSaving}
              pinned={NONE}
              options={locations.map((l) => ({ value: l.id, label: l.name }))}
            />
            <SearchableSelect
              label="Kind"
              fullWidth
              value={tagKind}
              onChange={setTagKind}
              disabled={tagSaving}
              pinned={NONE}
              options={KIND_OPTIONS}
            />
            <TextField
              label="External case reference (optional)"
              fullWidth
              value={tagRef}
              onChange={(e) => setTagRef(e.target.value)}
              disabled={tagSaving}
              helperText="An opaque case number to correlate later — no patient info."
            />
          </Stack>
          {tagError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {tagError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagEvent(null)} disabled={tagSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitTag()}
            disabled={tagSaving}
          >
            {tagSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disableTarget !== null}
        onClose={() => (mutatingId ? null : setDisableTarget(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Disable this{' '}
          {disableTarget?.kind === 'session' ? 'recording' : 'baseline'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            It will be excluded from scores and faculty comparisons, but nothing
            is deleted. It stays on the timeline, dimmed, and you can restore it
            at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisableTarget(null)}
            disabled={mutatingId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDisable()}
            color="error"
            variant="contained"
            disabled={mutatingId !== null}
          >
            {mutatingId !== null ? 'Disabling…' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    actions,
    dialogs,
    error: downloadError ?? actionError,
    departments,
    locations,
  };
}
